import "../style.css";
import "leaflet/dist/leaflet.css";
import lod_ from "lodash";
import { MapContainer, Marker, TileLayer, useMap } from "react-leaflet";
import { Icon as IconLeaflet } from "leaflet";
import { Checkbox, Icon, IconButton, Tooltip } from "@mui/material";
import { useEffect } from "react";
import MDBox from "components/Basics/MDBox";
import { useDispatch } from "react-redux";
import GoogleActions from "redux-react/actions/googleActions";
import MDTypography from "components/Basics/MDTypography";
import { t } from "i18next";

const MAP_ZOOM = 13;

/**
 * Recenters the map automatically when the latitude or longitude changes
 */
const RecenterAutomatically = ({ lat, lng }) => {
	const map = useMap();
	useEffect(() => {
		map.setZoom(MAP_ZOOM);
		map.setView([lat, lng]);
	}, [lat, lng]);
	return null;
};

const marker = new IconLeaflet({
	iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
	iconSize: [25, 41],
	iconAnchor: [12, 41],
	popupAnchor: [1, -34],
	shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
	shadowSize: [41, 41]
});

const redMarker = new IconLeaflet({
	iconUrl:
		"data:image/svg+xml;base64," +
		btoa(`
		<svg xmlns="http://www.w3.org/2000/svg" width="25" height="40" viewBox="0 0 25 40">
			<path fill="#dc3545" stroke="#fff" stroke-width="2" d="M12.5 0C5.6 0 0 5.6 0 12.5c0 8.8 12.5 27.5 12.5 27.5s12.5-18.7 12.5-27.5C25 5.6 19.4 0 12.5 0zm0 17.5c-2.8 0-5-2.2-5-5s2.2-5 5-5 5 2.2 5 5-2.2 5-5 5z"/>
		</svg>
	`),
	iconSize: [25, 40],
	iconAnchor: [12, 40],
	shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
	shadowSize: [41, 41]
});

const greenMarker = new IconLeaflet({
	iconUrl:
		"data:image/svg+xml;base64," +
		btoa(`
		<svg xmlns="http://www.w3.org/2000/svg" width="25" height="40" viewBox="0 0 25 40">
			<path fill="#35dc72ff" stroke="#fff" stroke-width="2" d="M12.5 0C5.6 0 0 5.6 0 12.5c0 8.8 12.5 27.5 12.5 27.5s12.5-18.7 12.5-27.5C25 5.6 19.4 0 12.5 0zm0 17.5c-2.8 0-5-2.2-5-5s2.2-5 5-5 5 2.2 5 5-2.2 5-5 5z"/>
		</svg>
	`),
	iconSize: [25, 40],
	iconAnchor: [12, 40],
	shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
	shadowSize: [41, 41]
});

/**
 * Interface step to select nearby shops
 * @param {object} form Current form state
 * @param {Function} handleChange Function to update form state
 * @param {Function} setStepValid Function to set the step validity
 * @param {object} resource Resource information
 * @returns {JSX.Element} The Shops step component
 */
const ShopInterfaceShops = ({ form, handleChange, setStepValid, resource }) => {
	const nearbyShopQuery =
		resource?.format?.nearbyShopQuery || t("FORMS.INTERFACE.SHOPS.nearbyShopQuery");
	const dispatch = useDispatch();

	/**
	 * Handles the selection or deselection of a shop from the list
	 * @param {object} shop The selected shop
	 */
	const handleShopSelect = shop => {
		if (form.competitorsGoogleID.includes(shop.id)) {
			const filtered = form.competitorsGoogleID.filter(id => id !== shop.id);
			handleChange("competitorsGoogleID", filtered);
		} else {
			handleChange("competitorsGoogleID", [...form.competitorsGoogleID, shop.id]);
		}
	};

	/**
	 * Search for nearby shops using Google API
	 */
	const searchNearbyShops = () => {
		dispatch(
			GoogleActions.mapsSearchText(
				nearbyShopQuery,
				{
					query: {
						locationBias: {
							circle: {
								center: {
									latitude: form.coordinates?.lat,
									longitude: form.coordinates?.long
								},
								radius: 10000
							}
						}
					},
					options: {
						matchShopsDB: true
					}
				},
				res => {
					handleChange("nearbyShops", res);
				}
			)
		);
	};

	/**
	 * Get the center coordinates for the map
	 * @returns {Array} Center coordinates for the map
	 */
	const getCenterCoordinates = () => {
		if (form.places?.length === 0 || !Array.isArray(form.places)) {
			return [48.866667, 2.333333]; // Default to Paris
		}

		if (
			!lod_.isNil(form.googleID) &&
			!lod_.isNil(form.coordinates?.lat) &&
			!lod_.isNil(form.coordinates?.long)
		) {
			return [form.coordinates.lat, form.coordinates.long];
		}

		const latitudes = form.places.map(c => c.location.latitude);
		const longitudes = form.places.map(c => c.location.longitude);
		const avgLat = latitudes.reduce((a, b) => a + b, 0) / latitudes.length;
		const avgLng = longitudes.reduce((a, b) => a + b, 0) / longitudes.length;
		return [avgLat, avgLng];
	};

	useEffect(() => {
		setStepValid(true);

		if (lod_.isNil(form.nearbyShops) || lod_.isEmpty(form.nearbyShops)) {
			searchNearbyShops();
		}
	}, []);

	return (
		<MDBox
			display="flex"
			flex="1"
			justifyContent="space-between"
			alignItems="stretch"
			gap={2}
			sx={{
				overflowY: "auto"
			}}
		>
			<MDBox flex="1" display="flex" flexDirection="column" overflowY="auto">
				<MDBox
					mb={1}
					p={2}
					borderRadius="lg"
					border="1px solid #eee"
					sx={{
						backgroundColor: "#f0f0f0"
					}}
				>
					<MDBox display="flex" justifyContent="space-between" alignItems="center">
						<MDBox fontWeight="bold">{form.name}</MDBox>
					</MDBox>
					<MDBox fontSize="0.8em" color="text">
						{form.address}
					</MDBox>
				</MDBox>

				<MDBox borderRadius="lg" overflow="hidden" flex="1">
					<MapContainer center={getCenterCoordinates()} zoom={MAP_ZOOM}>
						<TileLayer
							url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
							attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
						/>

						{/* Display competitors marker */}
						{form.nearbyShops.map((item, key) => {
							const isSelected = form.competitorsGoogleID.includes(item.id);
							return (
								<Marker
									key={key}
									position={[item.location.latitude, item.location.longitude]}
									icon={isSelected ? redMarker : marker}
									eventHandlers={{
										click: () => {
											handleShopSelect(item);
										}
									}}
								></Marker>
							);
						})}

						<Marker
							position={[form.coordinates?.lat, form.coordinates?.long]}
							icon={greenMarker}
						></Marker>

						<RecenterAutomatically
							lat={getCenterCoordinates()[0]}
							lng={getCenterCoordinates()[1]}
						/>
					</MapContainer>
				</MDBox>
			</MDBox>
			<MDBox flex="1" sx={{ display: "flex", flexDirection: "column", overflowY: "auto" }}>
				<MDBox display="flex" justifyContent="space-between" alignItems="center">
					<MDBox>
						<MDTypography variant="h6">
							{t("FORMS.INTERFACE.SHOPS.selectShopsTitle", {
								nearbyShopQuery
							})}
						</MDTypography>

						<MDTypography mb={2} variant="body2" color="text">
							{t("FORMS.INTERFACE.SHOPS.selectShops")}
						</MDTypography>
					</MDBox>
					<Tooltip title={t("FORMS.INTERFACE.SHOPS.refreshNearbyShops")} placement="top">
						<IconButton onClick={searchNearbyShops}>
							<Icon>refresh</Icon>
						</IconButton>
					</Tooltip>
				</MDBox>

				<MDBox sx={{ display: "flex", flexDirection: "column", overflowY: "auto" }}>
					{lod_.isEmpty(form.nearbyShops) && (
						<MDBox
							m={2}
							color="text"
							display="flex"
							justifyContent="center"
							alignItems="center"
							sx={{
								height: "100%"
							}}
						>
							<MDTypography variant="body2">
								{t("FORMS.INTERFACE.SHOPS.noNearbyShopsFound")}
							</MDTypography>
						</MDBox>
					)}
					{form.nearbyShops.map((item, key) => {
						return (
							<MDBox
								key={key}
								mb={1}
								p={2}
								borderRadius="lg"
								border="1px solid #eee"
								onClick={() => handleShopSelect(item)}
								bgColor={form.competitorsGoogleID.includes(item.id) ? "info" : "transparent"}
								sx={{
									cursor: "pointer"
								}}
								display="flex"
								alignItems="center"
								gap={1}
							>
								<Checkbox checked={form.competitorsGoogleID.includes(item.id)} />
								<MDBox>
									<MDBox display="flex" justifyContent="space-between" alignItems="center">
										<MDBox
											fontWeight="bold"
											color={form.competitorsGoogleID.includes(item.id) ? "white" : "text"}
										>
											{item.displayName.text}
										</MDBox>
									</MDBox>
									<MDBox
										fontSize="0.8em"
										color={form.competitorsGoogleID.includes(item.id) ? "white" : "text"}
									>
										{item.formattedAddress}
									</MDBox>
								</MDBox>
							</MDBox>
						);
					})}
				</MDBox>
			</MDBox>
		</MDBox>
	);
};

export default ShopInterfaceShops;
